import { useEffect, useMemo, useRef, useState } from 'react';
import { Bold, Captions, ClipboardCopy, ClipboardPaste, FileDown, FileUp, Italic, Link2, Plus, RotateCcw, Trash2, Unlink, VolumeX } from 'lucide-react';
import {
  COLOR_RANGES, CROSSFADE_ALIGN_LABELS, MARKER_COLORS, NEUTRAL_COLOR, TICKS_PER_SECOND, TRANSITION_KINDS, type ColorCorrection, TRANSITION_LABELS, allAudioTracks, angleCovers, audioTrackOf, clipDuration, framesToTimecode, formatTimecode, gapsOf, layoutVideo, maxTransitionTicks, ops,
  ografLayoutSize, parseSubtitles, renderAtData, sameAspect, serializeSrt, serializeVtt, ticksPerFrame, ticksToFrames, transitionBorrow, crossfadeLimits,
  DEFAULT_SUBTITLE_RULES, SIDECAR_CONTENT_TYPES, SIDECAR_FORMATS, SIDECAR_LABELS, effectiveCueStyle, legacyAlign, newCueAt, sequenceDuration, serializeTtml, setCueEdge, subtitleFileName,
  type Cue, type CueAlign, type CueEditContext, type CueStylePatch, type SidecarFormat,
  type CrossfadeAlign, type GraphicTemplateDTO, type ItemRef, type SourceDurations, type TransitionBorrow, type LayerClip, type SequenceDocument, type Size, type TransitionKind, type VideoClip,
} from '@prism/contracts';
import { allMuted, clipItems, crossfadeTarget, linkState, relinkTarget, type CommandId } from './editCommands';
import { describeItems, selectionSpan } from './selection';
import { useFormatView } from './formatContext';
import { EditingFormat, FormatFramingControls, LayerMotionControls, ShownIn } from './edit-inspector';
import { SubtitleHeightRow } from './SubtitleHeight';
import { useGraphic, useTemplateBundle } from '../../api/hooks';
import { GraphicPreview } from '../../components/GraphicPreview';
import { EditGraphicButton } from '../../graphics/EditGraphicButton';
import { editorDefaultsFor } from '../../graphics/editorDefaults';
import { carryToVersion, isOgrafTemplate, newerVersion } from '../../graphics/ograf';
import { OgrafInspector } from './OgrafInspector';
import { toast } from '../../components/Toasts';
import type { Selection } from './Timeline';
import { createStore, type PlayheadClock } from './playheadClock';
import { FollowButton } from '../../components/FollowButton';
import { useFollowPlayhead } from '../../lib/followList';
import { usePref } from '../../lib/prefs';
import { CueRow, PlacementGrid, Popover, StyleFields, SUBTITLE_IMPORT_ACCEPT, TrackStyleEditor, cueKeyResult, importNameParts, trackMetrics, cueTimecode, focusCueText, focusedCueCaret, toggleInlineTag, useSubtitleKeys, type CueEdge } from '../../subtitles';

const S = TICKS_PER_SECOND;
const secs = (t: number) => (t / S).toFixed(2);

function download(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

type Apply = (fn: (d: SequenceDocument) => SequenceDocument, coalesce?: string) => void;

export function Inspector({ doc, apply, seal = () => {}, selection, setSelection, templates, playhead, setPlayhead, canEdit, sourceSizes, items = [], onCommand, lengths, silent, alphaNote }: {
  doc: SequenceDocument;
  apply: Apply;
  /** Ends an undo gesture (slider released, field left), 0.96.0. */
  seal?: () => void;
  /** The item shown (1.7.3: the primary item of the selection). */
  selection: Selection;
  setSelection: (s: Selection) => void;
  templates: GraphicTemplateDTO[];
  playhead: number;
  setPlayhead: (t: number) => void;
  canEdit: boolean;
  /** 1.7.0: the sources' picture sizes by version id (the upscale badge); left out or unknown: no badge. */
  sourceSizes?: Map<string, Size>;
  /** 1.7.3: every selected item; with several, a summary with the common actions instead of one item's inspector. */
  items?: ItemRef[];
  /** 1.7.3: run an editing command on the selection (editCommands.ts). */
  onCommand?: (id: CommandId) => void;
  /** 1.8.0: the sources' lengths (crossfades across the cut, transitions that use extra media) and which are silent. */
  lengths?: SourceDurations;
  silent?: (versionId: string) => boolean;
  /** 1.8.1: Safari, and the clip's source has a transparent (VP9 alpha) proxy: its transparency shows in Chrome and Edge. */
  alphaNote?: (versionId: string) => boolean;
}) {
  const tc = (t: number) => formatTimecode(framesToTimecode(ticksToFrames(t, doc.frameRate), doc.frameRate));
  if (items.length > 1) return <MultiInspector doc={doc} items={items} canEdit={canEdit} onCommand={onCommand} />;
  const clip = selection && (selection.kind === 'video' || selection.kind === 'audio' || selection.kind === 'layer')
    ? [...doc.video, ...allAudioTracks(doc).flatMap((t) => t.clips), ...(doc.layers ?? []).flatMap((l) => l.clips)].find((c) => c.id === selection.id) : null;
  const graphic = selection?.kind === 'graphic' ? doc.graphics.find((g) => g.id === selection.id) : null;
  const marker = selection?.kind === 'marker' ? doc.markers?.find((m) => m.id === selection.id) : null;
  const gap = selection?.kind === 'gap' ? gapsOf(doc).find((g) => g.id === selection.id) : null;
  /** 1.7.3: Delete lifts (the editor's command); without the editor, the old remove. */
  const remove = (id: string) => { if (onCommand) onCommand('delete'); else { apply((d) => ops.removeClip(d, id)); setSelection(null); } };

  if (gap) {
    return (
      <div className="inspector-pane" data-testid="gap-inspector">
        <p className="section-label" style={{ margin: 0 }}>Gap on V1</p>
        <dl className="kv">
          <dt>From</dt><dd className="mono">{tc(gap.start)}</dd>
          <dt>To</dt><dd className="mono">{tc(gap.end)}</dd>
          <dt>Length</dt><dd className="mono">{secs(gap.end - gap.start)} s</dd>
        </dl>
        <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn sm" data-testid="close-gap" onClick={() => (onCommand ? onCommand('ripple-delete') : apply((d) => ops.closeGap(d, gap.id)))}
            title="Move this and the later clips up (with Sync on, the other tracks too)">Close the gap</button>
        </fieldset>
        <span className="help">Black in the monitor and the export. Delete or Shift+Delete closes it too.</span>
      </div>
    );
  }

  if (clip) {
    const audioTrack = audioTrackOf(doc, clip.id);
    const audioNumber = audioTrack ? allAudioTracks(doc).find((t) => t.id === audioTrack)?.number ?? 2 : null;
    const isA2 = audioTrack !== null;
    const layerIndex = (doc.layers ?? []).findIndex((l) => l.clips.some((c) => c.id === clip.id));
    const storyline = !isA2 && layerIndex < 0 ? (clip as VideoClip) : null;
    const relink = storyline?.soundDetached ? relinkTarget(doc, [{ kind: 'video', id: storyline.id }]) : null;
    return (
      <div className="inspector-pane" data-testid="clip-inspector">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <p className="section-label" style={{ margin: 0 }}>{isA2 ? `Audio clip (A${audioNumber})` : layerIndex >= 0 ? `Clip on V${layerIndex + 2}` : storyline?.soundDetached ? 'Storyline clip (V1, sound unlinked)' : 'Storyline clip (V1 + A1)'}</p>
          {!isA2 && <EditingFormat doc={doc} />}
        </div>
        <h3 style={{ margin: 0 }}>{clip.label}</h3>
        <dl className="kv">
          <dt>Source in</dt><dd className="mono">{tc(clip.sourceInTicks)}</dd>
          <dt>Source out</dt><dd className="mono">{tc(clip.sourceOutTicks)}</dd>
          <dt>Duration</dt><dd className="mono">{secs(clipDuration(clip))} s</dd>
          {storyline?.gapBeforeTicks ? <><dt>Gap before</dt><dd className="mono">{secs(storyline.gapBeforeTicks)} s</dd></> : null}
        </dl>
        {!isA2 && alphaNote?.(clip.versionId) && <p className="help" data-testid="clip-alpha-browser" style={{ margin: 0 }}>Transparency shows in Chrome and Edge</p>}
        <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {storyline?.gapBeforeTicks ? (
            <button className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => apply((d) => ops.closeGap(d, storyline.id))} data-testid="close-gap"
              title="Black before this clip: close it and move this and the later clips up">Close the gap before</button>
          ) : null}
          {/* 1.7.3: the sound of a V1 clip as a clip of its own on A2–A8, and back. */}
          {storyline && !storyline.soundDetached && (
            <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} data-testid="unlink-sound" onClick={() => apply((d) => ops.unlink(d, storyline.id))}
              title="Put this clip's sound on an audio track of its own, to move, trim or delete it apart from the picture (⇧⌘L)"><Unlink size={13} /> Unlink sound</button>
          )}
          {storyline?.soundDetached && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn ghost sm" data-testid="relink-sound" title="Put the sound back into this clip (it must line up with the picture)"
                onClick={() => {
                  if (!relink?.audioId || relink.problem) { toast(relink?.problem ?? 'Pick the sound on an audio track', 'error'); return; }
                  apply((d) => ops.relink(d, relink.clipId, relink.audioId!));
                }}><Link2 size={13} /> Relink sound</button>
              {relink?.problem && <span className="help" data-testid="relink-problem">{relink.problem}</span>}
            </div>
          )}
          {storyline && !storyline.gapBeforeTicks && <TransitionControls doc={doc} clip={storyline} apply={apply} lengths={lengths} />}
          {storyline?.multicam && <AngleSwitcher doc={doc} clip={storyline} apply={apply} />}
          {!isA2 && <FormatFramingControls doc={doc} clip={clip as VideoClip | LayerClip} layer={layerIndex >= 0} apply={apply} seal={seal} playhead={playhead} setPlayhead={setPlayhead}
            sourceSize={sourceSizes?.get(clip.versionId) ?? null} />}
          {layerIndex >= 0 && <LayerControls doc={doc} clip={clip as LayerClip} layerIndex={layerIndex} apply={apply} seal={seal} playhead={playhead} setPlayhead={setPlayhead} canEdit={canEdit} />}
          {!isA2 && <ColorControls clip={clip as VideoClip | LayerClip} apply={apply} seal={seal} />}
          {!isA2 && (
            <>
              <p className="section-label" style={{ margin: '4px 0 -4px' }}>Picture fades</p>
              <div className="field-row" data-testid="picture-fades">
                <div className="field"><label htmlFor="vfin">Fade in (s)</label><input id="vfin" className="input" type="number" min={0} step={0.1} value={secs(clip.videoFadeInTicks ?? 0)} onBlur={seal}
                  onChange={(e) => apply((d) => ops.updateClip(d, clip.id, { videoFadeInTicks: Math.round(Number(e.target.value) * S) }), `vfin:${clip.id}`)} /></div>
                <div className="field"><label htmlFor="vfout">Fade out (s)</label><input id="vfout" className="input" type="number" min={0} step={0.1} value={secs(clip.videoFadeOutTicks ?? 0)} onBlur={seal}
                  onChange={(e) => apply((d) => ops.updateClip(d, clip.id, { videoFadeOutTicks: Math.round(Number(e.target.value) * S) }), `vfout:${clip.id}`)} /></div>
              </div>
              <span className="help" style={{ marginTop: -6 }}>{layerIndex >= 0 ? 'From and to transparent.' : 'From and to black, after and before any transition.'} The fade handles on the timeline set the picture and the sound fade together.</span>
            </>
          )}
          {/* 1.8.0: a clip whose source has no sound has no sound controls. */}
          {silent?.(clip.versionId) ? <p className="help" data-testid="clip-no-sound" style={{ margin: 0 }}>This clip&apos;s source has no sound.</p> : (
            <>
              <p className="section-label" style={{ margin: '4px 0 -4px' }}>Sound</p>
              <div className="field">
                <label htmlFor="gain">Gain {clip.gainDb > 0 ? '+' : ''}{clip.gainDb} dB</label>
                <input id="gain" type="range" min={-30} max={12} step={1} value={clip.gainDb} onPointerUp={seal} onKeyUp={seal}
                  onChange={(e) => apply((d) => ops.updateClip(d, clip.id, { gainDb: Number(e.target.value) }), `gain:${clip.id}`)} />
              </div>
              <label className="toggles"><span><input type="checkbox" checked={clip.muted} onChange={(e) => apply((d) => ops.updateClip(d, clip.id, { muted: e.target.checked }))} /> <VolumeX size={13} /> Mute</span></label>
              <div className="field-row">
                <div className="field"><label htmlFor="fin">Sound fade in (s)</label><input id="fin" className="input" type="number" min={0} step={0.1} value={secs(clip.fadeInTicks)} onBlur={seal}
                  onChange={(e) => apply((d) => ops.updateClip(d, clip.id, { fadeInTicks: Math.round(Number(e.target.value) * S) }), `fin:${clip.id}`)} /></div>
                <div className="field"><label htmlFor="fout">Sound fade out (s)</label><input id="fout" className="input" type="number" min={0} step={0.1} value={secs(clip.fadeOutTicks)} onBlur={seal}
                  onChange={(e) => apply((d) => ops.updateClip(d, clip.id, { fadeOutTicks: Math.round(Number(e.target.value) * S) }), `fout:${clip.id}`)} /></div>
              </div>
              {(storyline || isA2) && <CrossfadeRow doc={doc} clipRef={{ kind: storyline ? 'video' : 'audio', id: clip.id }} apply={apply} lengths={lengths} onCommand={onCommand} />}
              {/* 1.8.0: a clip on a video track that still plays its own sound (made before 1.8.0) can hand it to an audio track. */}
              {layerIndex >= 0 && !clip.muted && (
                <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} data-testid="sound-to-track" onClick={() => onCommand?.('sound-to-track')}
                  title="Put this clip's sound on an audio track, linked to it, and mute the clip"><Unlink size={13} /> Move sound to an audio track</button>
              )}
            </>
          )}
          <button className="btn ghost sm danger" style={{ alignSelf: 'flex-start' }} data-testid="remove-clip" title={storyline ? 'Delete it and leave a gap (Delete); Shift+Delete closes up' : 'Delete it (Delete)'}
            onClick={() => remove(clip.id)}><Trash2 size={13} /> Remove clip</button>
        </fieldset>
      </div>
    );
  }

  if (graphic) {
    const t = templates.find((x) => x.id === graphic.templateId && x.version === graphic.templateVersion);
    if (isOgrafTemplate(t)) {
      return <OgrafInspector key={graphic.id} doc={doc} graphic={graphic} template={t} templates={templates} apply={apply} setSelection={setSelection} playhead={playhead} setPlayhead={setPlayhead} canEdit={canEdit} tc={tc} />;
    }
    return <GraphicInspector key={graphic.id} doc={doc} graphic={graphic} template={t} templates={templates} apply={apply} setSelection={setSelection} playhead={playhead} canEdit={canEdit} tc={tc} />;
  }

  if (marker) {
    return (
      <div className="inspector-pane" data-testid="marker-inspector">
        <p className="section-label">Marker at {tc(marker.atTicks)}</p>
        <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="field"><label htmlFor="marker-label">Name</label>
            <input id="marker-label" className="input" maxLength={200} value={marker.label} onBlur={seal} data-testid="marker-label"
              onChange={(e) => apply((d) => ops.updateMarker(d, marker.id, { label: e.target.value }), `marker:${marker.id}`)} />
          </div>
          <div className="field"><label htmlFor="marker-color">Colour</label>
            <select id="marker-color" className="select" value={marker.color ?? 'red'} onChange={(e) => apply((d) => ops.updateMarker(d, marker.id, { color: e.target.value === 'red' ? undefined : e.target.value as NonNullable<typeof marker.color> }))}>
              {MARKER_COLORS.map((c) => <option key={c} value={c}>{c[0]!.toUpperCase() + c.slice(1)}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn ghost sm" onClick={() => setPlayhead(marker.atTicks)}>Go to marker</button>
            <button className="btn ghost sm danger" data-testid="marker-delete" onClick={() => { apply((d) => ops.removeMarker(d, marker.id)); setSelection(null); }}><Trash2 size={13} /> Delete marker</button>
          </div>
        </fieldset>
        <span className="help">Markers are kept with the sequence and listed in the EDL as locators.</span>
      </div>
    );
  }

  // 1.7.4: the shortcuts moved to the Keys tab; with nothing selected the Inspector says only what to do.
  return (
    <div className="inspector-pane">
      <p className="muted" style={{ margin: 0 }} data-testid="inspector-hint">Select a clip, audio, graphic or marker on the timeline to adjust it · Keys lists the shortcuts</p>
    </div>
  );
}

/**
 * Several items selected (1.7.3): how many, and the actions they share: Delete (lift), Ripple delete, Link or Unlink,
 * Mute, Copy and Cut. The full inspector is for one item.
 */
function MultiInspector({ doc, items, canEdit, onCommand }: { doc: SequenceDocument; items: ItemRef[]; canEdit: boolean; onCommand?: (id: CommandId) => void }) {
  const links = linkState(doc, items);
  const clips = clipItems(items).length > 0;
  const muted = allMuted(doc, items);
  const span = selectionSpan(doc, items);
  const run = (id: CommandId) => onCommand?.(id);
  return (
    <div className="inspector-pane" data-testid="multi-inspector">
      <p className="section-label" style={{ margin: 0 }}>Selection</p>
      <h3 style={{ margin: 0 }} data-testid="multi-count">{items.length} items</h3>
      <p className="muted" style={{ margin: 0 }}>{describeItems(items)}{span ? ` · ${secs(span.end - span.start)} s` : ''}</p>
      <div className="multi-actions" role="group" aria-label="Actions for the selection">
        <button className="btn ghost sm" onClick={() => run('copy')} data-testid="multi-copy"><ClipboardCopy size={13} /> Copy</button>
        {canEdit && (
          <>
            <button className="btn ghost sm" onClick={() => run('cut')} data-testid="multi-cut">Cut</button>
            <button className="btn ghost sm danger" onClick={() => run('delete')} data-testid="multi-delete" title="Delete, leaving gaps on V1 (Delete)"><Trash2 size={13} /> Delete</button>
            <button className="btn ghost sm danger" onClick={() => run('ripple-delete')} data-testid="multi-ripple" title="Delete and close up (Shift+Delete)">Ripple delete</button>
            {links.canLink && <button className="btn ghost sm" onClick={() => run('link')} data-testid="multi-link" title="Select, move and delete them together (⌘L)"><Link2 size={13} /> Link</button>}
            {links.canUnlink && <button className="btn ghost sm" onClick={() => run('unlink')} data-testid="multi-unlink" title="⇧⌘L"><Unlink size={13} /> Unlink</button>}
            {clips && <button className="btn ghost sm" onClick={() => run('mute')} data-testid="multi-mute"><VolumeX size={13} /> {muted ? 'Unmute' : 'Mute'}</button>}
          </>
        )}
      </div>
      <span className="help">Drag any of them to move them all. Option-click picks one clip of a linked group; Esc clears the selection.</span>
    </div>
  );
}

const secsOf = (t: number) => Math.round((t / S) * 100) / 100;

/** Copy color / Paste color (0.96.0): one correction kept while the editor is open. */
const colorClipboard = createStore<ColorCorrection | null>(null);
function useColorClipboard(): ColorCorrection | null {
  const [v, setV] = useState(colorClipboard.get());
  useEffect(() => colorClipboard.subscribe(setV), []);
  return v;
}

const COLOR_FIELDS: Array<{ key: keyof ColorCorrection; label: string; fmt: (v: number) => string }> = [
  { key: 'exposure', label: 'Exposure', fmt: (v) => `${v > 0 ? '+' : ''}${v.toFixed(2)} stops` },
  { key: 'contrast', label: 'Contrast', fmt: (v) => `${v > 0 ? '+' : ''}${Math.round(v * 100)}` },
  { key: 'saturation', label: 'Saturation', fmt: (v) => `${Math.round(v * 100)}%` },
  { key: 'temperature', label: 'Temperature (cool – warm)', fmt: (v) => `${v > 0 ? '+' : ''}${Math.round(v * 100)}` },
  { key: 'tint', label: 'Tint (green – magenta)', fmt: (v) => `${v > 0 ? '+' : ''}${Math.round(v * 100)}` },
];

/**
 * Colour correction of a storyline or layer clip (0.96.0): exposure, contrast, saturation, temperature and tint,
 * the same maths in the preview and the export (contracts color.ts). One undo step per drag or field edit.
 */
function ColorControls({ clip, apply, seal }: { clip: VideoClip | LayerClip; apply: Apply; seal: () => void }) {
  const cur = clip.color ?? NEUTRAL_COLOR;
  const copied = useColorClipboard();
  const set = (key: keyof ColorCorrection, value: number) => {
    if (!Number.isFinite(value)) return;
    apply((d) => {
      const now = [...d.video, ...(d.layers ?? []).flatMap((l) => l.clips)].find((c) => c.id === clip.id)?.color ?? NEUTRAL_COLOR;
      return ops.setColor(d, clip.id, { ...now, [key]: value });
    }, `color-${key}:${clip.id}`);
  };
  return (
    <div className="color-controls" data-testid="color-controls">
      <p className="section-label" style={{ margin: '4px 0 0' }}>Color</p>
      {COLOR_FIELDS.map(({ key, label, fmt }) => {
        const r = COLOR_RANGES[key];
        return (
          <div className="field" key={key}>
            <label htmlFor={`cc-${key}`}>{label} <span className="faint mono">{fmt(cur[key])}</span></label>
            <div className="color-row">
              <input id={`cc-${key}`} type="range" min={r.min} max={r.max} step={r.step} value={cur[key]} data-testid={`color-${key}`}
                onChange={(e) => set(key, Number(e.target.value))} onPointerUp={seal} onKeyUp={seal} />
              <input className="input mono" type="number" min={r.min} max={r.max} step={r.step} value={Number(cur[key].toFixed(2))} aria-label={`${label} value`} data-testid={`color-${key}-value`}
                onChange={(e) => set(key, Number(e.target.value))} onBlur={seal} />
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button className="btn ghost sm" disabled={!clip.color} onClick={() => apply((d) => ops.setColor(d, clip.id, null))} data-testid="color-reset"><RotateCcw size={13} /> Reset</button>
        <button className="btn ghost sm" disabled={!clip.color} onClick={() => colorClipboard.set(clip.color ?? null)} data-testid="color-copy" title="Copy this clip's correction"><ClipboardCopy size={13} /> Copy color</button>
        <button className="btn ghost sm" disabled={!copied} onClick={() => { if (copied) apply((d) => ops.setColor(d, clip.id, copied)); }} data-testid="color-paste" title="Give this clip the copied correction"><ClipboardPaste size={13} /> Paste color</button>
      </div>
    </div>
  );
}

/** The transition into a storyline clip from the clip before it. */
function TransitionControls({ doc, clip, apply, lengths }: { doc: SequenceDocument; clip: VideoClip; apply: (fn: (d: SequenceDocument) => SequenceDocument) => void; lengths?: SourceDurations }) {
  const i = doc.video.findIndex((c) => c.id === clip.id);
  if (i <= 0) return <p className="faint" style={{ margin: 0, fontSize: 12 }}>The first clip starts with a cut. Transitions go on the clips after it.</p>;
  const prev = doc.video[i - 1]!;
  const t = clip.transitionIn;
  // 1.8.0: the limit of the clips as edited (without what the transition borrowed).
  const max = maxTransitionTicks({ ...prev, sourceOutTicks: prev.sourceOutTicks - (t?.tailTicks ?? 0) }, { ...clip, sourceInTicks: clip.sourceInTicks + (t?.headTicks ?? 0) }, doc.frameRate);
  const applied = layoutVideo(doc)[i]!.transition;
  const set = (kind: TransitionKind | 'none', seconds: number) => apply((d) => ops.setTransition(d, clip.id, kind === 'none' ? null : { kind, durationTicks: Math.round(seconds * S) }, lengths));
  const borrow = transitionBorrow(doc, clip.id);
  return (
    <div className="field" data-testid="transition-controls">
      <label htmlFor="transition">Transition from “{prev.label}”</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <select id="transition" className="select" value={t?.kind ?? 'none'} onChange={(e) => set(e.target.value as TransitionKind | 'none', t ? t.durationTicks / S : Math.min(1, max / S))} disabled={max <= 0}>
          <option value="none">Cut</option>
          {TRANSITION_KINDS.map((k) => <option key={k} value={k}>{TRANSITION_LABELS[k]}</option>)}
        </select>
        {t && <input className="input" type="number" min={0.04} max={secsOf(max)} step={0.04} style={{ width: 90 }} aria-label="Transition length in seconds"
          value={secsOf(t.durationTicks)} onChange={(e) => set(t.kind, Number(e.target.value))} />}
      </div>
      <span className="help">
        {max <= 0 ? 'These clips are too short for a transition.' : `Up to ${secsOf(max)} s (half the shorter clip).`}
        {t && applied && applied.ticks < t.durationTicks ? ` Shortened to ${secsOf(applied.ticks)} s to fit.` : ''}
      </span>
      {borrow && <span className="help" data-testid="transition-media">{borrowText(borrow, prev.label, clip.label, doc.frameRate)}</span>}
    </div>
  );
}

/**
 * What a transition takes from its clips (1.8.0): "Uses 0.50 s of extra media on each side", or which side gives how
 * much and what still overlaps (the only part that shortens the sequence).
 */
function borrowText(b: TransitionBorrow, outgoing: string, incoming: string, rate: SequenceDocument['frameRate']): string {
  const tpf = ticksPerFrame(rate);
  const s = (t: number) => (t / S).toFixed(2);
  if (!b.recorded) return `The clips overlap for the transition, so the sequence is ${s(b.overlapTicks)} s shorter.`;
  const parts: string[] = [];
  if (!b.overlapTicks && Math.abs(b.headTicks - b.tailTicks) <= tpf) parts.push(`Uses ${s((b.headTicks + b.tailTicks) / 2)} s of extra media on each side.`);
  else {
    const used = [b.tailTicks ? `${s(b.tailTicks)} s after “${outgoing}”` : '', b.headTicks ? `${s(b.headTicks)} s before “${incoming}”` : ''].filter(Boolean);
    if (used.length) parts.push(`Uses ${used.join(' and ')} of extra media.`);
    if (b.overlapTicks) parts.push(`${s(b.overlapTicks)} s overlaps, so the sequence is that much shorter.`);
  }
  return parts.join(' ');
}

/**
 * The crossfade into a V1 or audio clip (1.8.0): "Crossfade in: 1.00 s · Centred on cut", with the choice of where it
 * sits (the context menu's Centre on cut, Start at cut, End at cut) and Remove; for a clip without one, Crossfade.
 */
function CrossfadeRow({ doc, clipRef, apply, lengths, onCommand }: { doc: SequenceDocument; clipRef: ItemRef; apply: Apply; lengths?: SourceDurations; onCommand?: (id: CommandId) => void }) {
  const t = crossfadeTarget(doc, { primary: clipRef, items: [clipRef] }, lengths);
  if (!t || (!t.has && t.problem)) return null;
  if (!t.has) return <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} data-testid="inspector-crossfade" onClick={() => onCommand?.('crossfade')}>Crossfade (⇧⌘D)</button>;
  return (
    <div className="field" data-testid="inspector-crossfade">
      <label htmlFor="xfade-align">Crossfade in: {(t.ticks / S).toFixed(2)} s · {CROSSFADE_ALIGN_LABELS[t.align].replace('Centre', 'Centred')}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <select id="xfade-align" className="select" value={t.align} data-testid="crossfade-align"
          onChange={(e) => apply((d) => ops.setCrossfade(d, clipRef, t.ticks, e.target.value as CrossfadeAlign, lengths))}>
          {(['center', 'start', 'end'] as const).map((a) => (
            <option key={a} value={a} disabled={Boolean(crossfadeLimits(doc, clipRef, { align: a, durations: lengths }).problem)}>{CROSSFADE_ALIGN_LABELS[a]}</option>
          ))}
        </select>
        <button className="btn ghost sm" onClick={() => onCommand?.('crossfade')} data-testid="crossfade-remove">Remove</button>
      </div>
    </div>
  );
}

/** Show another camera angle of a multicam clip at the same moment. */
function AngleSwitcher({ doc, clip, apply }: { doc: SequenceDocument; clip: VideoClip; apply: (fn: (d: SequenceDocument) => SequenceDocument) => void }) {
  const g = (doc.multicam ?? []).find((x) => x.id === clip.multicam!.groupId);
  if (!g) return null;
  const from = g.angles[clip.multicam!.angle]!.startTicks + clip.sourceInTicks;
  const to = from + clipDuration(clip);
  return (
    <div className="field" data-testid="angle-switcher">
      <label>Angle from “{g.label}”</label>
      <div className="tags">
        {g.angles.map((a, k) => {
          const ok = angleCovers(g, k, from, to);
          return (
            <button key={k} type="button" className={`tag toggle ${clip.multicam!.angle === k ? 'on' : ''}`} aria-pressed={clip.multicam!.angle === k} disabled={!ok}
              title={ok ? a.label : `${a.label} has no picture for this moment`} onClick={() => apply((d) => ops.switchAngle(d, clip.id, k))} data-testid={`angle-${k + 1}`}>
              {k + 1} · {a.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** A layer clip's opacity, size and position (1.7.0: in the active format, with motion keys), where it is shown, and its track. */
function LayerControls({ doc, clip, layerIndex, apply, seal, playhead, setPlayhead, canEdit }: {
  doc: SequenceDocument; clip: LayerClip; layerIndex: number; apply: Apply; seal: () => void; playhead: number; setPlayhead: (t: number) => void; canEdit: boolean;
}) {
  const layers = doc.layers ?? [];
  return (
    <>
      <LayerMotionControls doc={doc} clip={clip} apply={apply} seal={seal} playhead={playhead} setPlayhead={setPlayhead} />
      <ShownIn doc={doc} itemId={clip.id} hideIn={clip.hideIn} apply={apply} canEdit={canEdit} />
      <div className="field">
        <label htmlFor="layer-move">Track</label>
        <select id="layer-move" className="select" value={layers[layerIndex]!.id} onChange={(e) => apply((d) => ops.moveLayerClip(d, clip.id, clip.recordInTicks, e.target.value))}>
          {layers.map((l, k) => <option key={l.id} value={l.id}>V{k + 2}{l.label !== `V${k + 2}` ? ` · ${l.label}` : ''}</option>)}
        </select>
        <span className="help">Higher tracks are drawn on top. Transparent areas of alpha-channel media show the tracks below.</span>
      </div>
    </>
  );
}

export function SequenceSubtitles(props: {
  doc: SequenceDocument; apply: Apply; selection: Selection; setSelection: (s: Selection) => void;
  playhead: number; setPlayhead: (t: number) => void; canEdit: boolean;
  /** Ends an undo gesture (the height slider released), 1.7.1. */
  seal?: () => void;
  /** 1.9.0: the selected cues (Shift-click selects several) and how to set them. */
  cueIds?: string[];
  setCueIds?: (ids: string[]) => void;
  /** 1.9.0: the live playhead (the subtitle keys read it at the press) and ⌥P's playback of a cue. */
  now?: () => number;
  playRange?: (fromTicks: number, toTicks: number) => void;
  /** 1.9.3: the live playhead (the cues at it are marked as it plays) and whether the sequence is playing (Follow). */
  clock?: PlayheadClock;
  playing?: boolean;
}) {
  return <SubtitleEditor {...props} />;
}

function GraphicInspector({ doc, graphic, template, templates, apply, setSelection, playhead, canEdit, tc }: {
  doc: SequenceDocument; graphic: SequenceDocument['graphics'][number]; template: GraphicTemplateDTO | undefined; templates: GraphicTemplateDTO[];
  apply: Apply; setSelection: (s: Selection) => void; playhead: number; canEdit: boolean; tc: (t: number) => string;
}) {
  const bundle = useTemplateBundle(template?.id, template?.version);
  const newer = newerVersion(templates, graphic);
  // A graphics asset's template is asset-<asset id>, also when its template is not registered (yet).
  const sourceAsset = template?.asset?.id ?? (/^asset-[0-9a-f-]{36}$/.test(graphic.templateId) ? graphic.templateId.slice('asset-'.length) : null);
  // A graphic without a registered template (one made in PRISM Graphics, until 0.93.0) is named from its asset.
  const source = useGraphic(sourceAsset, !template && Boolean(sourceAsset));
  const local = Math.max(0, Math.min(graphic.durationTicks, playhead - graphic.recordInTicks)) / S;
  const previewT = playhead >= graphic.recordInTicks && playhead < graphic.recordInTicks + graphic.durationTicks ? local : (template?.manifest.previewTimeSeconds ?? 1);
  // The size it lays out at (1.0.0-rc.11): another shape than the frame's is fitted into it (1.7.0: the active format's frame).
  const frame = useFormatView(doc).activeFormat;
  const authored = template ? (isOgrafTemplate(template) ? ografLayoutSize(template.ograf, frame) : { width: template.manifest.width, height: template.manifest.height }) : null;
  return (
    <div className="inspector-pane" data-testid="graphic-inspector">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <p className="section-label" style={{ margin: 0 }}>Graphic · {template?.manifest.name ?? source.data?.name ?? graphic.templateId} v{graphic.templateVersion}</p>
        <EditingFormat doc={doc} />
      </div>
      {authored && !sameAspect(authored, frame) && (
        <div className="banner info" data-testid="graphic-fitted-note">
          <div className="banner-body">Authored for {authored.width}×{authored.height}; shown fitted in {frame.width}×{frame.height}.</div>
        </div>
      )}
      {newer && (
        <div className="banner warning" data-testid="newer-version-banner">
          <div className="banner-body" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ flex: 1 }}>Version {newer.version} of this graphic is available. This clip stays on v{graphic.templateVersion} until you update it.</span>
            {canEdit && <button className="btn sm" data-testid="update-graphic-version" onClick={() => { const c = carryToVersion(newer, graphic); apply((d) => ops.retargetGraphic(d, graphic.id, { templateVersion: newer.version, data: c.data })); }}>Update to v{newer.version}</button>}
          </div>
        </div>
      )}
      {/* Opens the clip's source asset; saving makes a new version and leaves this clip (and the version it uses) as it is. */}
      {sourceAsset && (
        <EditGraphicButton assetId={sourceAsset} title={template?.asset?.title ?? source.data?.name} className="btn ghost sm" testId="clip-edit-graphic" style={{ alignSelf: 'flex-start' }}
          defaults={editorDefaultsFor(doc)} />
      )}
      {template && bundle.data && (
        <div className="graphic-preview-box"><GraphicPreview bundle={bundle.data} manifest={template.manifest} data={renderAtData(graphic.data)} t={previewT} durationSeconds={graphic.durationTicks / S} /></div>
      )}
      <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {template?.manifest.fields.map((f) => (
          <div className="field" key={f.id}>
            <label htmlFor={`gf-${f.id}`}>{f.label}{f.required && <span className="req">*</span>}</label>
            {f.type === 'color'
              ? <input id={`gf-${f.id}`} type="color" value={String(graphic.data[f.id] ?? f.default)} onChange={(e) => apply((d) => ops.updateGraphic(d, graphic.id, { data: { [f.id]: e.target.value } }))} />
              : <input id={`gf-${f.id}`} className="input" maxLength={f.maxLength} value={String(graphic.data[f.id] ?? '')} onChange={(e) => apply((d) => ops.updateGraphic(d, graphic.id, { data: { [f.id]: e.target.value } }), `gfield:${graphic.id}:${f.id}`)} />}
          </div>
        ))}
        <div className="field-row">
          <div className="field"><label>Starts</label><span className="mono readonly-value">{tc(graphic.recordInTicks)}</span></div>
          <div className="field"><label htmlFor="gdur">Duration (s)</label>
            <input id="gdur" className="input" type="number" min={0.2} step={0.2} value={secs(graphic.durationTicks)} onChange={(e) => apply((d) => ops.updateGraphic(d, graphic.id, { durationTicks: Math.round(Number(e.target.value) * S) }))} />
          </div>
        </div>
        <ShownIn doc={doc} itemId={graphic.id} hideIn={graphic.hideIn} apply={apply} canEdit={canEdit} />
        <button className="btn ghost sm danger" style={{ alignSelf: 'flex-start' }} onClick={() => { apply((d) => ops.removeClip(d, graphic.id)); setSelection(null); }}><Trash2 size={13} /> Remove graphic</button>
      </fieldset>
      <span className="help">Rendered for export in an isolated browser using this exact template version; the preview runs the same code.</span>
    </div>
  );
}

/**
 * The sequence's subtitles (1.9.0): the shared CueRows (subtitles/CueRow.tsx) in sequence timecode from 00:00:00:00,
 * 3 s new cues, the placement grid, cue styles and the track style (subtitles.style), and the subtitle keys while the
 * Subtitles side tab is open. Every edit goes through `apply`, so undo works as for the rest of the sequence.
 */
function SubtitleEditor({ doc, apply, seal = () => {}, selection, setSelection, playhead, setPlayhead, canEdit, cueIds, setCueIds, now, playRange, clock, playing = false }: {
  doc: SequenceDocument; apply: Apply; seal?: () => void; selection: Selection; setSelection: (s: Selection) => void;
  playhead: number; setPlayhead: (t: number) => void; canEdit: boolean;
  cueIds?: string[]; setCueIds?: (ids: string[]) => void; now?: () => number; playRange?: (fromTicks: number, toTicks: number) => void;
  clock?: PlayheadClock; playing?: boolean;
}) {
  const formats = useFormatView(doc).formats;
  const subs = doc.subtitles;
  const rate = doc.frameRate;
  const trackStyle = subs?.style ?? {};
  const [edge, setEdge] = useState<CueEdge | null>(null);
  const ids = cueIds ?? (selection?.kind === 'cue' ? [selection.id] : []);
  const selectIds = (next: string[], e: CueEdge | null = null) => {
    setEdge(e);
    if (setCueIds) setCueIds(next);
    else setSelection(next[0] ? { kind: 'cue', id: next[0] } : null);
  };
  const durationTicks = useMemo(() => sequenceDuration(doc), [doc]);
  const ctx: CueEditContext = useMemo(() => ({ frameRate: rate, durationTicks: null, trackStyle }), [rate, trackStyle]);
  const metrics = useMemo(() => trackMetrics(subs?.cues ?? [], trackStyle, undefined), [subs?.cues, trackStyle]);
  const sorted = useMemo(() => [...(subs?.cues ?? [])].sort((a, b) => a.startTicks - b.startTicks), [subs?.cues]);
  // 1.9.3: the cues at the live playhead, read from the clock (re-rendering only when they change, not every frame).
  const [atPlayhead, setAtPlayhead] = useState<string[]>([]);
  useEffect(() => {
    const at = (t: number) => sorted.filter((c) => t >= c.startTicks && t < c.endTicks).map((c) => c.id);
    const update = (t: number) => setAtPlayhead((prev) => { const next = at(t); return next.length === prev.length && next.every((id, i) => id === prev[i]) ? prev : next; });
    update(clock ? clock.get() : playhead);
    return clock?.subscribe(update);
  }, [clock, sorted, playhead]);
  const [follow, setFollow] = usePref('editor.subtitles.follow', false);
  const cueList = useRef<HTMLDivElement>(null);
  useFollowPlayhead({ enabled: follow, playing, targetId: atPlayhead[0] ?? null, list: cueList, attr: 'data-cue' });
  const ensure = (d: SequenceDocument) => d.subtitles ?? { language: 'en', label: 'English', cues: [] };
  const setCues = (fn: (cues: Cue[]) => Cue[], key?: string) =>
    apply((d) => { const s = ensure(d); const next = fn(s.cues); return next === s.cues && d.subtitles ? d : ops.setSubtitles(d, { ...s, cues: next }); }, key);
  const styleCues = (targets: string[], patch: CueStylePatch | null) => apply((d) => ops.setCueStyle(d, targets, patch));
  const live = () => now?.() ?? playhead;
  const selectedCues = (subs?.cues ?? []).filter((c) => ids.includes(c.id));
  const trackLook = effectiveCueStyle(trackStyle, { text: '' });

  async function importFile(file: File) {
    const parsed = parseSubtitles(await file.text());
    if (!parsed.cues.length) { toast('No cues found. Is this an SRT, WebVTT or TTML file?', 'error'); return; }
    const name = importNameParts(file.name);
    apply((d) => ops.setSubtitles(d, { language: name.language ?? parsed.language ?? d.subtitles?.language ?? 'en', label: d.subtitles?.label ?? name.label, cues: parsed.cues, ...(parsed.style ? { style: parsed.style } : {}) }));
    seal();
    toast(`Imported ${parsed.cues.length} cues${parsed.errors.length ? ` (${parsed.errors.length} skipped)` : ''}`);
  }
  function downloadAs(f: SidecarFormat) {
    if (!subs) return;
    try {
      const text = f === 'srt' ? serializeSrt(subs.cues, subs.style) : f === 'vtt' ? serializeVtt(subs.cues, subs.language, subs.style)
        : serializeTtml({ language: subs.language, label: subs.label, style: subs.style }, subs.cues, { profile: f === 'ttml' ? 'imsc' : 'ebu-tt-d', frameRate: rate });
      download(subtitleFileName(subs.label || 'subtitles', subs.language, f), text, SIDECAR_CONTENT_TYPES[f]);
    } catch (err) { toast(`Could not write ${SIDECAR_LABELS[f]}: ${(err as Error).message}`, 'error'); }
  }
  function addCue() {
    const r = newCueAt(subs?.cues ?? [], live(), ctx);
    if (!r) { toast('No room for a cue here: the next cue starts too soon', 'error'); return; }
    setCues(() => r.cues);
    seal();
    selectIds([r.cue.id]);
    focusCueText(r.cue.id);
  }
  function inline(tag: 'i' | 'b') {
    const el = document.activeElement;
    if (el instanceof HTMLTextAreaElement && el.dataset.cueText) {
      const id = el.dataset.cueText;
      const r = toggleInlineTag(el.value, el.selectionStart, el.selectionEnd, tag);
      setCues((cs) => cs.map((c) => (c.id === id ? { ...c, text: r.text } : c)));
      seal();
      requestAnimationFrame(() => { try { el.setSelectionRange(r.start, r.end); } catch { /* gone */ } });
      return;
    }
    const want = new Set(ids);
    setCues((cs) => cs.map((c) => (want.has(c.id) ? { ...c, text: toggleInlineTag(c.text, 0, 0, tag).text } : c)));
    seal();
  }

  useSubtitleKeys(true, (action) => {
    const edits = action.action !== 'play-cue' && action.action !== 'previous-cue' && action.action !== 'next-cue';
    if (edits && !canEdit) return false;
    // ⌥, and ⌥. act on a cue only when one is selected; otherwise they slip the selected clip as before.
    if (action.action === 'nudge' && ids.length !== 1) return false;
    const cues = doc.subtitles?.cues ?? [];
    const r = cueKeyResult(action, { cues, selection: { ids, edge }, playhead: live(), caret: focusedCueCaret(ids[0]), ctx });
    if (!r) return false;
    if (r.cues) {
      const next = r.cues;
      // A run of nudges on one cue is one undo step, as slip's.
      apply((d) => ops.setSubtitles(d, { ...ensure(d), cues: next }), action.action === 'nudge' ? `cue-nudge:${ids[0]}` : undefined);
      if (action.action !== 'nudge') seal();
    }
    if (r.selection) selectIds(r.selection.ids, r.selection.edge);
    if (r.seek !== undefined) setPlayhead(r.seek);
    if (r.play) playRange?.(r.play.from, r.play.to);
    if (r.focus) focusCueText(r.focus);
    return true;
  });

  const alignOf = (c: Cue): CueAlign | null => c.style?.align ?? legacyAlign(c.text) ?? null;
  const gridValue = selectedCues.length && selectedCues.every((c) => alignOf(c) === alignOf(selectedCues[0]!)) ? alignOf(selectedCues[0]!) : null;
  const gridMixed = selectedCues.length > 1 && !selectedCues.every((c) => alignOf(c) === alignOf(selectedCues[0]!));
  const common = <K extends 'color' | 'background' | 'size'>(k: K) => { const v = selectedCues.map((c) => c.style?.[k]); return v.length && v.every((x) => x === v[0]) ? v[0] : undefined; };
  const sharedStyle = { color: common('color'), background: common('background'), size: common('size') };
  const none = !canEdit || !selectedCues.length;

  return (
    <div className="inspector-pane subtitle-editor-v19" data-testid="subtitle-editor">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <p className="section-label" style={{ margin: 0, flex: 1 }}><Captions size={12} /> Sequence subtitles</p>
        <FollowButton on={follow} onChange={setFollow} testId="editor-subtitle-follow" />
        {canEdit && (
          <label className="btn ghost sm" style={{ cursor: 'pointer' }} title="SRT, WebVTT or TTML (IMSC, EBU-TT-D, DFXP)"><FileUp size={13} /> Import
            <input type="file" accept={SUBTITLE_IMPORT_ACCEPT} hidden data-testid="sequence-import-subtitles" onChange={(e) => { const f = e.target.files?.[0]; if (f) void importFile(f); e.target.value = ''; }} />
          </label>
        )}
        {subs?.cues.length ? (
          <Popover label="Download the track" button={<><FileDown size={13} /> Download</>} testId="sequence-subtitle-download" width={230}>
            {(close) => (
              <div className="sub-menu" role="group" aria-label="Download">
                {SIDECAR_FORMATS.map((f) => <button key={f} type="button" className="row-menu-item" data-format={f} onClick={() => { close(); downloadAs(f); }}>{SIDECAR_LABELS[f]}</button>)}
                <p className="faint" style={{ margin: '6px 0 0', fontSize: 11 }}>SRT and WebVTT keep the placement and colour; background and size are kept in the TTML files only.</p>
              </div>
            )}
          </Popover>
        ) : null}
      </div>
      {subs && (
        <div className="field-row">
          <div className="field"><label htmlFor="sub-lang">Language</label><input id="sub-lang" className="input" value={subs.language} disabled={!canEdit} onChange={(e) => apply((d) => ops.setSubtitles(d, { ...d.subtitles!, language: e.target.value }), 'sub-lang')} /></div>
          <div className="field"><label htmlFor="sub-label">Label</label><input id="sub-label" className="input" value={subs.label} disabled={!canEdit} onChange={(e) => apply((d) => ops.setSubtitles(d, { ...d.subtitles!, label: e.target.value }), 'sub-label')} /></div>
        </div>
      )}
      {canEdit && subs && (
        <div className="sub-toolbar" role="toolbar" aria-label="Cue style" data-testid="sequence-subtitle-toolbar">
          <button type="button" className="btn ghost sm icon" title="Italic (⌘I)" aria-label="Italic" disabled={none} onMouseDown={(e) => e.preventDefault()} onClick={() => inline('i')}><Italic size={13} /></button>
          <button type="button" className="btn ghost sm icon" title="Bold (⌘B)" aria-label="Bold" disabled={none} onMouseDown={(e) => e.preventDefault()} onClick={() => inline('b')}><Bold size={13} /></button>
          <span className="sub-toolbar-sep" />
          <Popover label="Colour" button={<><span className="style-dot" style={{ background: sharedStyle.color ?? trackLook.color }} aria-hidden /> Colour</>} disabled={none} testId="seq-toolbar-color" width={250}>
            {() => <StyleFields only="color" value={sharedStyle} fallback={trackLook} testId="seq-toolbar-color" onChange={(p) => styleCues(ids, p)} />}
          </Popover>
          <Popover label="Background" button={<>Background</>} disabled={none} testId="seq-toolbar-background" width={250}>
            {() => <StyleFields only="background" value={sharedStyle} fallback={trackLook} testId="seq-toolbar-background" onChange={(p) => styleCues(ids, p)} />}
          </Popover>
          <Popover label="Size" button={<>Size</>} disabled={none} testId="seq-toolbar-size" width={290}>
            {() => <StyleFields only="size" value={sharedStyle} fallback={trackLook} testId="seq-toolbar-size" onChange={(p) => styleCues(ids, p)} />}
          </Popover>
          <PlacementGrid value={gridValue} mixed={gridMixed} trackAlign={trackLook.align} disabled={none} testId="seq-toolbar-grid" onChange={(a) => styleCues(ids, { align: a })} />
          <span style={{ flex: 1 }} />
          <TrackStyleEditor value={trackStyle} onChange={(s) => apply((d) => ops.setSubtitleStyle(d, s))} testId="sequence-track-style" />
        </div>
      )}
      {/* 1.7.1: where the line sits in each format (also by dragging it in the monitor). */}
      <div className="subs-position" data-testid="subtitle-position">
        <p className="section-label" style={{ margin: 0 }}>Position</p>
        {formats.map((f) => <SubtitleHeightRow key={f.id} f={f} apply={apply} seal={seal} disabled={!canEdit} showChip={formats.length > 1} />)}
        <span className="help">The bottom row’s height: drag the subtitle line in the monitor to move it in the format shown; double-click it for the default. Top and middle cues use the placement grid.</span>
      </div>
      <div className="cue-list" data-testid="cue-list" ref={cueList}>
        {sorted.map((c, i) => (
          <CueRow key={c.id} cue={c} index={i} rate={rate} startFrames={0} trackStyle={trackStyle} metrics={metrics.get(c.id)!} rules={DEFAULT_SUBTITLE_RULES}
            selected={ids.includes(c.id)} atPlayhead={atPlayhead.includes(c.id)} activeEdge={ids.length === 1 && ids[0] === c.id ? edge : null} readOnly={!canEdit}
            onSelect={({ extend }) => {
              const next = extend ? (ids.includes(c.id) ? ids.filter((x) => x !== c.id) : [...ids, c.id]) : [c.id];
              selectIds(next, !extend && ids.length === 1 && ids[0] === c.id ? edge : null);
              if (!extend) setPlayhead(c.startTicks);
            }}
            onEdge={(e, t) => { setCues((cs) => setCueEdge(cs, c.id, e, t, ctx)); seal(); selectIds([c.id], e); }}
            onActiveEdge={(e) => selectIds([c.id], e)}
            onText={(text) => setCues((cs) => cs.map((x) => (x.id === c.id ? { ...x, text } : x)), `cue-text:${c.id}`)}
            onStyle={(patch) => styleCues(ids.includes(c.id) ? ids : [c.id], patch)}
            onDelete={() => { setCues((cs) => cs.filter((x) => x.id !== c.id)); seal(); selectIds(ids.filter((x) => x !== c.id)); }} />
        ))}
        {!subs?.cues.length && <p className="muted" style={{ margin: 0 }}>No subtitles yet. Add cues at the playhead (⌥N) or import an SRT, WebVTT or TTML file. Right-to-left text is supported.</p>}
      </div>
      {canEdit && (
        <button className="btn sm" style={{ alignSelf: 'flex-start' }} data-testid="add-cue" onClick={addCue} title="A 3 s cue at the playhead (⌥N)">
          <Plus size={13} /> Add cue at {cueTimecode(playhead, rate, 0)}
        </button>
      )}
      {durationTicks > 0 && subs?.cues.some((c) => c.endTicks > durationTicks) && <span className="warn-text">Some cues run past the end of the sequence.</span>}
    </div>
  );
}
